
import React, { useState } from 'react';
import { requestPhoneVerification, confirmPhoneVerification } from '../../services/cognito';

interface PhoneVerificationModalProps {
    isOpen: boolean;
    onClose: () => void; // Should probably just close request
    onVerified: () => void;
}

const PhoneVerificationModal: React.FC<PhoneVerificationModalProps> = ({ isOpen, onClose, onVerified }) => {
    const [step, setStep] = useState<'REQUEST' | 'VERIFY'>('REQUEST');
    const [code, setCode] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handleSendCode = async () => {
        setIsLoading(true);
        setError('');
        try {
            await requestPhoneVerification();
            setStep('VERIFY');
        } catch (err: any) {
            setError(err.message || 'Failed to send verification code. Please ensure SMS is configured in Cognito.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerify = async () => {
        setIsLoading(true);
        setError('');
        try {
            await confirmPhoneVerification(code);
            onVerified();
        } catch (err: any) {
            setError(err.message || 'Verification failed.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6">
                    <div>
                        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                            <i className="fas fa-mobile-alt text-blue-600"></i>
                        </div>
                        <div className="mt-3 text-center sm:mt-5">
                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                Verify Phone Number
                            </h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500">
                                    {step === 'REQUEST'
                                        ? "We need to verify your phone number before you can add properties. Click below to send a code via SMS."
                                        : "Please enter the verification code sent to your phone."
                                    }
                                </p>
                            </div>
                            {error && <p className="mt-2 text-sm text-red-600 font-bold">{error}</p>}
                        </div>
                    </div>

                    {step === 'VERIFY' && (
                        <div className="mt-4">
                            <input
                                type="text"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md p-2 border"
                                placeholder="123456"
                            />
                        </div>
                    )}

                    <div className="mt-5 sm:mt-6">
                        {step === 'REQUEST' ? (
                            <button
                                type="button"
                                className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm"
                                onClick={handleSendCode}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Sending...' : 'Send Verification Code'}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="inline-flex justify-center w-full rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm"
                                onClick={handleVerify}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Verifying...' : 'Verify Code'}
                            </button>
                        )}
                        <button
                            type="button"
                            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none sm:text-sm"
                            onClick={onClose}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PhoneVerificationModal;
